/**
 * Quanto rimpicciolire il disegno del televisore per farlo stare nel riquadro vero.
 *
 * ## Il numero sullo schermo non è il numero che vede la pagina
 *
 * Le schermate del televisore sono costruite su una tela fissa di 1920×1080 (`layout.designWidth`).
 * Su Tizen quel numero coincide con quello che il browser riporta, e per questo non si era mai
 * visto niente. Su **Android TV** no: lo schermo è 1920×1080 fisici a densità 320, cioè ×2, e la
 * pagina riceve un riquadro di **960×540**.
 *
 * Il risultato non è un'interfaccia piccola: è un'interfaccia **tagliata**. Nella schermata di
 * attivazione la colonna di sinistra è larga 760 e riempie da sola quei 960 — il QR e il codice
 * stanno oltre il bordo destro, senza scorrimento e senza nessun errore da nessuna parte. Un box
 * Android TV non si può attivare, e il difetto si vede solo guardandolo.
 *
 * ## Si prende il lato peggiore, non la larghezza
 *
 * `min` fra i due rapporti: con il solo rapporto delle larghezze, un riquadro largo e basso — una
 * finestra di browser trascinata, un televisore 21:9 — terrebbe la larghezza e taglierebbe il fondo,
 * cioè il posto dove stanno i tasti.
 *
 * ## Si ingrandisce anche, non solo si rimpicciolisce
 *
 * Su un 4K che riporta 3840 px CSS il fattore è 2, ed è giusto: la tela è una **unità di misura**,
 * non un massimo. Limitarla a 1 lascerebbe l'interfaccia in un francobollo al centro dello schermo.
 */

/** La tela su cui sono disegnate le schermate del televisore. */
export const TV_DESIGN_WIDTH = 1920;
export const TV_DESIGN_HEIGHT = 1080;

/**
 * Il fattore di scala, o `1` quando il riquadro non è ancora misurabile.
 *
 * Zero o un numero senza senso capita al primo disegno, prima che il motore abbia assestato la
 * pagina: `1` lascia le cose come sono per un istante, mentre uno `0` le farebbe sparire — e una
 * schermata vuota per mezzo secondo è indistinguibile da un'app che non parte.
 */
export function tvScale(
  viewportWidth: number,
  viewportHeight: number,
  designWidth: number = TV_DESIGN_WIDTH,
  designHeight: number = TV_DESIGN_HEIGHT,
): number {
  if (!Number.isFinite(viewportWidth) || !Number.isFinite(viewportHeight)) return 1;
  if (viewportWidth <= 0 || viewportHeight <= 0) return 1;

  return Math.min(viewportWidth / designWidth, viewportHeight / designHeight);
}
